import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Wheat, Loader2 } from "lucide-react";

const SHOW_DEMO = String(process.env.REACT_APP_SHOW_DEMO || "").toLowerCase() === "true";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(SHOW_DEMO ? "admin@srm.com" : "");
  const [password, setPassword] = useState(SHOW_DEMO ? "admin123" : "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 bg-stone-50">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-800 text-white">
              <Wheat size={26} />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold tracking-tight text-stone-900">SRM WMS</div>
              <div className="text-xs uppercase tracking-widest text-emerald-700">Warehouse System</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight text-stone-900">Sign in</h1>
          <p className="mt-2 text-sm text-stone-500">
            SRM Agri Warehouse LLP — Multi-warehouse operations & accounting.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                className="mt-1.5 h-12 w-full rounded-lg border border-stone-200 bg-white px-4 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1.5 h-12 w-full rounded-lg border border-stone-200 bg-white px-4 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white transition-colors hover:bg-emerald-900 active:scale-[0.99] disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Sign in
            </button>
          </form>

          {SHOW_DEMO && (
            <div className="mt-6 rounded-lg border border-stone-200 bg-white p-3 text-xs text-stone-500">
              <div className="font-semibold text-stone-700 mb-1">Demo accounts (local only)</div>
              admin@srm.com / admin123 (Super Admin)<br />
              accountant@srm.com / account123 · operator@srm.com / operator123
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Warehouse"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-emerald-950/70" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <h2 className="font-display text-4xl font-extrabold tracking-tight leading-tight">
            Real-time grain<br />warehouse control.
          </h2>
          <p className="mt-4 max-w-md text-emerald-100/90">
            Stacks, lots, ownership, labour, dala and party ledgers — one atomic, online-first system built for the
            way SRM actually operates.
          </p>
        </div>
      </div>
    </div>
  );
}
